import { z } from 'zod';

import { artifactKindSchema, artifactStatusSchema } from './artifacts.ts';

/**
 * The turn-event kinds the chat surface renders as parts of an assistant message, as opposed to
 * the spine's own `text`/`done`/`error`. They are ordinary event kinds — the log stores kinds as
 * text precisely so a new one is not a migration — and they carry a schema here because two
 * sides depend on the shape: the agent emits them and the SPA reconciles them.
 *
 * `status` is transient. It says what the agent is doing right now ("building your guide…") and
 * is deliberately not part of the conversation afterwards, so a replayed turn does not
 * re-narrate its own progress.
 *
 * `artifact` is persistent and keyed by `artifact_id`: successive events for one artifact
 * replace each other rather than accumulating, which is what turns an agent rewriting a
 * document into a document visibly changing instead of a list of snapshots.
 */
export const TURN_EVENT_ARTIFACT = 'artifact';

export const statusEventPayloadSchema = z.object({
  message: z.string().min(1).max(200),
});
export type StatusEventPayload = z.infer<typeof statusEventPayloadSchema>;

export const artifactEventPayloadSchema = z.object({
  artifact_id: z.uuid(),
  study_id: z.uuid(),
  kind: artifactKindSchema,
  /** Zero while the first version is still being written and nothing is stored yet. */
  version: z.int().nonnegative(),
  status: artifactStatusSchema,
  /**
   * The upstream changes the artifact is waiting on, or null — one window per upstream kind.
   *
   * Left `unknown` and lifted at the render site rather than parsed strictly here, because this
   * reader's contract is to return `null` for anything that does not match and the caller drops the
   * frame. A payload written before per-kind windows existed would therefore not fail loudly; the
   * event would simply vanish and the sidebar would stop moving, with nothing logged.
   */
  stale_reason: z.unknown(),
  /** The document as far as it has been written; absent when only the chrome moved. */
  content: z.unknown().optional(),
  /**
   * Still being written, so the sidebar shows it as arriving rather than as a stored version.
   *
   * Defaulted, because the agent's own writes are whole documents landing at once: there is nothing
   * partial to announce, so its emitter says nothing. Required, this field would make every artifact
   * event fail to parse — and a failed parse here is a dropped frame, so the sidebar would silently
   * never move, with no test failing and nothing in the log.
   */
  streaming: z.boolean().default(false),
  /**
   * The write matched what was already stored, so no version was added. This alone does not say
   * what the user should be told: see `resolved_staleness`.
   */
  deduped: z.boolean(),
  /**
   * A dedupe that also cleared the needs-attention flag — the agent regenerated the artifact
   * against the upstream change and concluded the content still stands. It has to be
   * distinguishable from a plain dedupe, because "nothing changed" and "nothing needed to
   * change, and that answers the change you were waiting on" are different facts and only the
   * second one unblocks approval.
   */
  resolved_staleness: z.boolean(),
});
export type ArtifactEventPayload = z.infer<typeof artifactEventPayloadSchema>;

/**
 * Validate on read, like stored content: these payloads cross a process boundary and are
 * replayed out of the log long after the code that wrote them, so the reader parses rather than
 * trusts. Returns `null` for anything that does not match, because a single malformed event must
 * not take down a stream whose remaining events are fine.
 */
export const parseArtifactEventPayload = (value: unknown): ArtifactEventPayload | null => {
  const parsed = artifactEventPayloadSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

export const parseStatusEventPayload = (value: unknown): StatusEventPayload | null => {
  const parsed = statusEventPayloadSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};
